/**
 * DataTable Component
 * Generic data table integrating useTable (TanStack React Table) with the existing UI components
 * Includes search, sortable headers, pagination, loading skeleton, and empty state
 */

import { type ColumnDef, flexRender } from '@tanstack/react-table';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { useTable } from '@/hooks/useTable';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { Skeleton } from '@/components/ui/Skeleton';
import styles from './DataTable.module.css';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  searchPlaceholder?: string;
  /** Esconde la barra de filtro client-side. Usar cuando el padre ya tiene su propio buscador (ej. server-side). */
  hideSearch?: boolean;
  onRowClick?: (row: TData) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  pageSize?: number;
}

function TableSkeleton({ columns: colCount, rows }: { columns: number; rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={`skeleton-row-${i.toString()}`} className="flex gap-4">
          {Array.from({ length: colCount }).map((_, j) => (
            <div key={`skeleton-cell-${j.toString()}`} className="flex-1">
              <Skeleton variant="text" height={16} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function DataTable<TData>({
  data,
  columns,
  searchPlaceholder = 'Buscar...',
  hideSearch = false,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No se encontraron resultados',
  pageSize = 10,
}: DataTableProps<TData>) {
  const { table, globalFilter, setGlobalFilter } = useTable({
    data,
    columns,
    pageSize,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton variant="rectangular" height={40} />
        <TableSkeleton columns={columns.length} rows={5} />
      </div>
    );
  }

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  return (
    <div className="space-y-4">
      {/* Search bar (oculta cuando el padre tiene su propio buscador) */}
      {!hideSearch && (
        <div className="relative w-72">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className={styles.searchInput}
          />
          {globalFilter && (
            <button
              type="button"
              onClick={() => setGlobalFilter('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-border bg-muted/50">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground"
                  >
                    {header.isPlaceholder ? null : (
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' ? (
                          <ArrowUp size={14} />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <ArrowDown size={14} />
                        ) : (
                          <ArrowUpDown size={14} className="opacity-30" />
                        )}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-sm text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`border-b border-border/50 transition-colors hover:bg-muted/30 ${
                    onRowClick ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 text-foreground">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {table.getFilteredRowModel().rows.length} resultado(s)
          </span>
          <Pagination
            currentPage={currentPage}
            totalPages={pageCount}
            onPageChange={(page) => table.setPageIndex(page - 1)}
          />
        </div>
      )}
    </div>
  );
}
